import { arcPath } from "./chart-geometry";
import { cn } from "./cn";
import "./gauge.css";
import "./dual-gauge.css";

export interface DualGaugeProps {
  label: string;
  outerRatio: number;
  innerRatio: number;
  outerDisplay: string;
  innerDisplay: string;
  /** Resolved color for the outer ring + its identity square. */
  outerColor: string;
  /** Resolved color for the inner ring + its identity square. */
  innerColor: string;
  note?: string;
  className?: string;
}

/**
 * Two concentric 240deg arcs on one gauge: an outer and an inner ratio of the
 * same base population (e.g. "of everyone who arrived, the share that did X"
 * and "... the share that did Y"). Values are ink; the colored square beside
 * each carries which ring it names — never color alone. Deliberately capped
 * at two rings: a third would need genuine categorical separation this
 * primitive doesn't carry (see chart-color.ts's slot cap) — small multiples
 * of single Gauges is the right answer past two.
 */
export function DualGauge({
  label,
  outerRatio,
  innerRatio,
  outerDisplay,
  innerDisplay,
  outerColor,
  innerColor,
  note,
  className,
}: DualGaugeProps) {
  const CX = 70;
  const CY = 70;
  const START = 240;
  const SWEEP = 240;
  const ring = (r: number, ratio: number) => {
    const clamped = Math.max(0, Math.min(ratio, 1));
    return clamped > 0 ? arcPath(CX, CY, r, START, START + SWEEP * Math.max(clamped, 0.02)) : null;
  };
  const outer = ring(56, outerRatio);
  const inner = ring(42, innerRatio);
  return (
    <figure className={cn("rc-gauge", className)}>
      <svg
        className="rc-gauge__svg"
        viewBox="0 0 140 128"
        role="img"
        aria-label={`${label}: outer ${outerDisplay}, inner ${innerDisplay}`}
      >
        <path
          className="rc-gauge__track rc-gauge__track--thin"
          d={arcPath(CX, CY, 56, START, START + SWEEP)}
        />
        <path
          className="rc-gauge__track rc-gauge__track--thin"
          d={arcPath(CX, CY, 42, START, START + SWEEP)}
        />
        {outer ? (
          <path
            className="rc-gauge__fill rc-gauge__fill--thin"
            style={{ stroke: outerColor }}
            d={outer}
          />
        ) : null}
        {inner ? (
          <path
            className="rc-gauge__fill rc-gauge__fill--thin"
            style={{ stroke: innerColor }}
            d={inner}
          />
        ) : null}
        <rect x={44} y={57} width={8} height={8} rx={2} fill={outerColor} />
        <text className="rc-gauge__value rc-gauge__value--dual" x={80} y={65} textAnchor="middle">
          {outerDisplay}
        </text>
        <rect x={44} y={74} width={8} height={8} rx={2} fill={innerColor} />
        <text
          className="rc-gauge__value rc-gauge__value--dual rc-gauge__value--muted"
          x={80}
          y={82}
          textAnchor="middle"
        >
          {innerDisplay}
        </text>
      </svg>
      <figcaption className="rc-gauge__label">
        {label}
        {note ? <span className="rc-gauge__note">{note}</span> : null}
      </figcaption>
    </figure>
  );
}
